import { useState } from 'react'
import { RadarCanvas } from './RadarCanvas'
import type { ScoutMapAgg } from '../types'

// Where a team's utility lands, every demo at once. Counts told us they throw 73 smokes; this says
// WHERE — and across a folder the repeat offenders are the standard setup. One demo is a round;
// seven demos is an execute.

const UTIL: Record<string, { color: string; label: string }> = {
  smokes: { color: '#c2c9d4', label: 'smokes' },
  molly: { color: '#f0862f', label: 'molotovs' },
  he: { color: '#e23b3b', label: 'HE' },
  flashes: { color: '#f2df8f', label: 'flashes' },
}
const ORDER = ['smokes', 'molly', 'flashes', 'he']

export function UtilMap({ maps, who }: { maps: ScoutMapAgg[]; who: string }) {
  const withNades = maps.filter((m) => m.nades?.length)
  const [mapIdx, setMapIdx] = useState(0)
  const [types, setTypes] = useState<Set<string>>(new Set(['smokes', 'molly']))
  // Which site's executes to draw. This is the question a coach actually asks — "show me their A
  // hits" — and it's why paths are tagged with the plant site rather than just dumped.
  const [site, setSite] = useState<string | null>('A')
  // CT holds answer the other half of the map: where they stand when you arrive.
  const [showSetups, setShowSetups] = useState(false)
  // Lineups: a line from where each grenade was thrown to where it landed. The cluster says WHAT
  // they smoke; this says from where, which is the part you can stand on and contest.
  const [showLineups, setShowLineups] = useState(false)
  // Where they actually plant, not just how often — the spot dictates the retake setup.
  const [showPlants, setShowPlants] = useState(false)
  // Two-storey maps (nuke) need a floor picker: one radar can't show both, and X/Y alone would
  // stack a lower-B player on top of an upper-A one.
  const [layer, setLayer] = useState<'upper' | 'lower'>('upper')
  if (!withNades.length) return null

  const m = withNades[Math.min(mapIdx, withNades.length - 1)]
  // A map is layered only if the parser actually tagged points — flat maps carry no layer at all.
  const layered = m.nades.some((n) => n.layer)
  const onLayer = (l?: string | null) => !layered || (l ?? 'upper') === layer
  const shown = m.nades.filter((n) => types.has(n.type) && onLayer(n.layer))
  // Only demos parsed since arcs were captured carry an origin; older ones just don't draw a line.
  const lineups = shown.filter((n) => n.fx !== undefined && n.fy !== undefined)
  const plants = (m.plantSpots ?? []).filter((p) => onLayer(p.layer))
  const sites = [...new Set((m.paths ?? []).map((p) => p.site).filter(Boolean))].sort() as string[]
  const paths = site === null ? [] : (m.paths ?? []).filter((p) => p.site === site)
  const radarMap = layered && layer === 'lower' ? `${m.map}_lower` : m.map
  const toggle = (t: string) =>
    setTypes((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {withNades.map((x, i) => (
          <button
            key={x.map}
            onClick={() => setMapIdx(i)}
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: i === mapIdx ? 'var(--accent)' : 'transparent',
              color: i === mapIdx ? 'var(--accent-ink)' : 'var(--text-dim)',
            }}
          >
            {x.map.replace('de_', '')}
            <span className="ml-1 opacity-60">{x.nades.length}</span>
          </button>
        ))}
        <span className="mx-1 text-text-faint">|</span>
        {ORDER.filter((t) => m.nades.some((n) => n.type === t)).map((t) => {
          const on = types.has(t)
          const n = m.nades.filter((x) => x.type === t).length
          return (
            <button
              key={t}
              onClick={() => toggle(t)}
              className="flex items-center gap-1.5 px-2 py-1 text-xs"
              style={{
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                background: on ? 'var(--surface-2)' : 'transparent',
                color: on ? 'var(--text)' : 'var(--text-faint)',
              }}
            >
              <span
                className="inline-block h-2.5 w-2.5"
                style={{ borderRadius: '50%', background: UTIL[t].color, opacity: on ? 1 : 0.3 }}
              />
              {UTIL[t].label} <span className="opacity-60">{n}</span>
            </button>
          )
        })}
        {sites.length > 0 && (
          <>
            <span className="mx-1 text-text-faint">|</span>
            <span className="text-[11px] text-text-faint">routes to</span>
            {sites.map((sname) => {
              const on = site === sname
              const n = (m.paths ?? []).filter((p) => p.site === sname).length
              return (
                <button
                  key={sname}
                  onClick={() => setSite(on ? null : sname)}
                  className="px-2 py-1 text-xs"
                  style={{
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? 'var(--accent-ink)' : 'var(--text-dim)',
                  }}
                >
                  {sname} <span className="opacity-60">{n}</span>
                </button>
              )
            })}
          </>
        )}
        {(m.setups?.length ?? 0) > 0 && (
          <button
            onClick={() => setShowSetups((v) => !v)}
            title="Where their CTs stand ~12s into the round, every round at once"
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: showSetups ? 'var(--loss)' : 'transparent',
              color: showSetups ? '#fff' : 'var(--text-dim)',
            }}
          >
            CT holds <span className="opacity-60">{m.setups.length}</span>
          </button>
        )}
        {lineups.length > 0 && (
          <button
            onClick={() => setShowLineups((v) => !v)}
            title="Line from where each grenade was thrown to where it landed"
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: showLineups ? 'var(--surface-2)' : 'transparent',
              color: showLineups ? 'var(--text)' : 'var(--text-dim)',
            }}
          >
            lineups <span className="opacity-60">{lineups.length}</span>
          </button>
        )}
        {plants.length > 0 && (
          <button
            onClick={() => setShowPlants((v) => !v)}
            title="Where they plant the bomb — bigger dot = more plants there"
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: showPlants ? 'var(--loss)' : 'transparent',
              color: showPlants ? '#fff' : 'var(--text-dim)',
            }}
          >
            plant spots <span className="opacity-60">{plants.reduce((s, p) => s + p.n, 0)}</span>
          </button>
        )}
        {layered && (
          <>
            <span className="mx-1 text-text-faint">|</span>
            {(['upper', 'lower'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLayer(l)}
                title={`${m.map.replace('de_', '')} is two-storey — ${l} floor has its own radar`}
                className="px-2 py-1 text-xs"
                style={{
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  background: layer === l ? 'var(--accent)' : 'transparent',
                  color: layer === l ? 'var(--accent-ink)' : 'var(--text-dim)',
                }}
              >
                {l}
              </button>
            ))}
          </>
        )}
        <span className="ml-auto text-[11px] text-text-faint">
          {m.demos} demo{m.demos === 1 ? '' : 's'}
          {m.demos < 3 ? <span className="ml-1 text-loss">thin</span> : null}
        </span>
      </div>

      <RadarCanvas
        map={radarMap}
        size={640}
        maxWidth={620}
        deps={[shown.length, paths.length, radarMap, site, showSetups, showLineups, showPlants, layer, [...types].join(',')]}
        paint={(ctx, project) => {
          // Routes first, under the utility — the util is thrown to support the route.
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          for (const pa of paths) {
            if (pa.pts.length < 2) continue
            ctx.strokeStyle = 'rgba(120,190,255,0.22)'
            ctx.lineWidth = 1.5
            // Break the line where the route changes floor, or it draws through a ceiling.
            ctx.beginPath()
            let pen = false
            pa.pts.forEach(([x, y], i) => {
              if (!onLayer(pa.layers?.[i])) {
                pen = false
                return
              }
              const [cx, cy] = project(x, y)
              if (pen) ctx.lineTo(cx, cy)
              else ctx.moveTo(cx, cy)
              pen = true
            })
            ctx.stroke()
            if (!onLayer(pa.layers?.[pa.pts.length - 1])) continue
            // Where the route ended — the contact point, which is what you set up against.
            const [ex, ey] = project(pa.pts[pa.pts.length - 1][0], pa.pts[pa.pts.length - 1][1])
            ctx.fillStyle = 'rgba(120,190,255,0.5)'
            ctx.beginPath()
            ctx.arc(ex, ey, 2, 0, Math.PI * 2)
            ctx.fill()
          }

          // CT hold positions — every round's setup stacked, so the recurring spots stand out.
          if (showSetups) {
            for (const su of m.setups ?? []) {
              for (let i = 0; i < su.pts.length; i++) {
                if (!onLayer(su.layers?.[i])) continue
                const [x, y] = su.pts[i]
                const [cx, cy] = project(x, y)
                ctx.fillStyle = 'rgba(255,110,110,0.30)'
                ctx.beginPath()
                ctx.arc(cx, cy, 4, 0, Math.PI * 2)
                ctx.fill()
              }
            }
          }

          // Plant spots: a bomb-red disc scaled by how many plants landed there. Drawn over holds
          // and routes because "this is where the bomb ends up" is the anchor a retake is built on.
          if (showPlants) {
            const maxN = Math.max(1, ...plants.map((p) => p.n))
            for (const p of plants) {
              const [cx, cy] = project(p.x, p.y)
              const r = 5 + 10 * (p.n / maxN)
              ctx.fillStyle = 'rgba(226,59,59,0.28)'
              ctx.beginPath()
              ctx.arc(cx, cy, r, 0, Math.PI * 2)
              ctx.fill()
              ctx.fillStyle = 'rgba(226,59,59,0.95)'
              ctx.font = `bold ${Math.round(9)}px Inter`
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              ctx.fillText(String(p.n), cx, cy)
            }
            ctx.textAlign = 'start'
            ctx.textBaseline = 'alphabetic'
          }

          // Lineups under the bloom: the line is context for the cluster, not a competitor to it.
          if (showLineups) {
            for (const n of lineups) {
              const [sx, sy] = project(n.fx!, n.fy!)
              const [ex, ey] = project(n.x, n.y)
              ctx.strokeStyle = `${UTIL[n.type]?.color ?? '#888'}44`
              ctx.lineWidth = 1
              ctx.beginPath()
              ctx.moveTo(sx, sy)
              ctx.lineTo(ex, ey)
              ctx.stroke()
              // A dot at the throwing end — that's the spot to hold, smoke off, or take away.
              ctx.fillStyle = `${UTIL[n.type]?.color ?? '#888'}99`
              ctx.beginPath()
              ctx.arc(sx, sy, 1.8, 0, Math.PI * 2)
              ctx.fill()
            }
          }

          // Additive, so overlap is the signal: one smoke is a round, twenty in the same spot is
          // the setup they run every time.
          ctx.globalCompositeOperation = 'lighter'
          for (const n of shown) {
            const [cx, cy] = project(n.x, n.y)
            const c = UTIL[n.type]?.color ?? '#888'
            const r = n.type === 'smokes' ? 15 : n.type === 'molly' ? 13 : 9
            const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
            g.addColorStop(0, `${c}66`)
            g.addColorStop(1, `${c}00`)
            ctx.fillStyle = g
            ctx.beginPath()
            ctx.arc(cx, cy, r, 0, Math.PI * 2)
            ctx.fill()
          }
          // A hard core on top of the bloom, so a single nade is still locatable.
          ctx.globalCompositeOperation = 'source-over'
          for (const n of shown) {
            const [cx, cy] = project(n.x, n.y)
            ctx.fillStyle = UTIL[n.type]?.color ?? '#888'
            ctx.beginPath()
            ctx.arc(cx, cy, 1.6, 0, Math.PI * 2)
            ctx.fill()
          }
        }}
      />
      <p className="mt-2 text-[11px] text-text-faint">
        Every {who} grenade from every demo in this folder, at once. Bright clusters are the setup
        they run every time — faint singles are one-offs.
        {paths.length > 0 && (
          <> Blue lines are their T-side routes into {site} (first 45s), dots where each run ended.</>
        )}
        {showSetups && <> Red dots are CT positions 12s into each round — their default hold.</>}
        {showPlants && <> Red discs are where the bomb gets planted, numbered by how many times.</>}
        {showLineups && <> Thin lines run from where each grenade was thrown to where it landed.</>}
      </p>
    </>
  )
}
